import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styles from "./CombinedMenu.module.scss";
import { Container, Menu, Icon } from "semantic-ui-react";
import { FormattedMessage, useIntl } from "react-intl";
import ModalPopup from "../../ModalPopup";
import { setModalOpen } from "../../../actions/siteActions";
import { PopupInner } from "../../../types/siteTypes";
import { CgShoppingCart } from "react-icons/cg";
import {
    cartItemsSelector,
    favouritesItemsSelector,
    isAuthenticatedSelector,
    userSelector,
} from "../../../utils/selectors";
import AdminMenu from "../SecondaryMenu/AdminMenu";
import AuthLinks from "../SecondaryMenu/AuthLinks";
import MenuInput from "../../Inputs/MenuInput";
import LanguageSelector from "../SecondaryMenu/LanguageSelector";

const CombinedMenu = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [keyword, setKeyword] = useState("");
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const isAuthenticated = useSelector((state) =>
        isAuthenticatedSelector(state)
    );
    const cartItems = useSelector((state) => cartItemsSelector(state));
    const favouritesItems = useSelector((state: any) =>
        favouritesItemsSelector(state)
    );
    const user = useSelector((state) => userSelector(state));
    const dispatch = useDispatch();

    const submitHandler = (e: any) => {
        e.preventDefault();
        if (keyword.trim()) {
            navigate(`/patterns?keyword=${keyword}`);
        } else {
            navigate("/");
        }
        setMobileMenuOpen(false);
        setSearchOpen(false);
    };

    const loggedInLinks = (
        <>
            <Menu.Item>
                {user && user.isAdmin && <AdminMenu />}
                <AuthLinks />
            </Menu.Item>
        </>
    );

    const guestLinks = (
        <>
            <Menu.Item>
                <span
                    onClick={() => {
                        dispatch(setModalOpen(PopupInner.LogIn));
                        setMobileMenuOpen(false);
                    }}
                    className={styles.link}
                >
                    <Icon name="user outline" />
                    <FormattedMessage id={"site.login"} defaultMessage={"Login"} />
                </span>
            </Menu.Item>
        </>
    );

    return (
        <nav className={styles.combinedMenu} >
            <Container>
                <div className={styles.board}>
                    <Link className={styles.logo} to={"/"}>
                        <img src="/icons/sewlight-logo.svg" alt="sewlight" />
                    </Link>
                    <button
                        className={styles.hamburger}
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label={intl.formatMessage({ id: "header.toggleMenu", defaultMessage: "Toggle menu" })}
                    >
                        <Icon name={mobileMenuOpen ? "close" : "bars"} size="large" />
                    </button>
                    <div className={`${styles.menuContent} ${mobileMenuOpen ? styles.mobileOpen : ''}`}>
                        <div className={styles.mainNav}>
                            <Link to="/patterns" onClick={() => setMobileMenuOpen(false)}>
                                <button className={styles.navButton}>
                                    <FormattedMessage
                                        id={"nav.patterns"}
                                        defaultMessage={"Sewing patterns"}
                                    />
                                </button>
                            </Link>
                        </div>
                        <div className={styles.rightSection}>
                            {isAuthenticated && favouritesItems > 0 && (
                                <Link
                                    className={`${styles.iconLink} ${styles.favouritesIconLink}`}
                                    to="/favourites"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <Icon name="heart" size="large" />
                                    <span className={`${styles.badge} ${styles.favouritesBadge}`}>
                                        {favouritesItems}
                                    </span>
                                </Link>
                            )}
                            {cartItems > 0 && (
                                <Link className={styles.iconLink} to="/cart" onClick={() => setMobileMenuOpen(false)}>
                                    <CgShoppingCart size={22} />
                                    <span className={styles.badge}>{cartItems}</span>
                                </Link>
                            )}
                            <div className={styles.searchContainer}>
                                {!searchOpen ? (
                                    <button
                                        className={styles.searchIcon}
                                        onClick={() => setSearchOpen(true)}
                                        aria-label={intl.formatMessage({ id: "header.search", defaultMessage: "Search" })}
                                    >
                                        <Icon name="search" size="large" />
                                    </button>
                                ) : (
                                    <form onSubmit={submitHandler} className={styles.searchForm}>
                                        <MenuInput
                                            onChange={(e: any) => setKeyword(e.target.value)}
                                            value={keyword}
                                            autoFocus
                                        >
                                            {intl.formatMessage({
                                                id: "nav.search",
                                                defaultMessage: "Search",
                                            })}
                                        </MenuInput>
                                        <button
                                            type="button"
                                            className={styles.closeSearch}
                                            onClick={() => {
                                                setSearchOpen(false);
                                                setKeyword("");
                                            }}
                                        >
                                            <Icon name="close" />
                                        </button>
                                    </form>
                                )}
                            </div>
                            <div className={styles.userSection}>
                                {isAuthenticated ? loggedInLinks : guestLinks}
                            </div>
                        </div>
                    </div>
            <LanguageSelector lang={intl.locale} />
                </div>
            </Container>
            <ModalPopup />
        </nav >
    );
};

export default CombinedMenu;
